import React, { useCallback, useMemo, useRef } from 'react';
import { CommunityOptionsType, TableHeight } from '../constants';
import { useCompareKeyMetricsChart } from '../hooks';
import { dealWithMentionsSentimentChart, getGameColorOrName } from '../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import './index.scss';
import { formatDateFunc } from '@op/utils/time';
import TableColumn from '../component/TableColumn';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import ProgressColumn from '../component/ProgressColumn';
import { decimalFormatPercentage, reviewsFormat } from '@op/utils/value';
import { NO_DATE } from '@op/constants';

export default function CommunityCharts({
  filter,
  chartMap,
  chartType = 'line',
  gameData,
  byType,
  tab,
  getOtherParams,
  sortBy,
  setSortBy,
}: {
  filter: any;
  chartType?: string;
  chartMap: any;
  byType: { value: string };
  tab: CommunityOptionsType;
  getOtherParams: (tab: CommunityOptionsType) => any;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  sortBy: SortBy<any>;
  setSortBy?: React.Dispatch<React.SetStateAction<SortBy<any>>>;
}) {
  const { formatMessage } = useLanguage();

  const getGameData = getGameColorOrName(gameData);

  const params = useMemo(() => {
    if (!byType.value) return {};
    const otherParams: any = getOtherParams(tab);
    return {
      ...filter,
      tab_type: tab,
      by_type: byType.value,
      stats_types: ['community'],
      ...otherParams,
    };
  }, [filter, byType]);

  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;
  const hasStatistics = Array.isArray(xAxisData) && xAxisData.length > 0;

  const { text: tooltip, format: formatData } = chartMap || {};

  const isEngagementRate = useMemo(
    () => byType.value === 'engagement_rate',
    [byType],
  );

  const valueFormat = useCallback(
    (value: number) =>
      isEngagementRate
        ? decimalFormatPercentage(value, '-')
        : reviewsFormat(Math.abs(value)),
    [isEngagementRate],
  );

  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: tooltip ? formatMessage({ id: tooltip }) : '',
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            value={cvm.value}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
            formateValue={valueFormat}
          ></ProgressColumn>
        ),
      },
    ],
    [formatMessage, getGameData, params?.date_type, tooltip, valueFormat],
  );

  const options = useMemo(() => {
    if (hasStatistics) {
      const legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      const seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
      }[] = [];

      yAxisData.map((item) => {
        const color = getGameData(item.name, 'color');
        seriesData.push({
          name: item.name,
          type: chartType,
          stack: chartType === 'bar' ? 'total' : '',
          data: item.data,
          barMaxWidth: '70px',
          itemStyle: { color },
        });
        legendData.push({
          name: item.name,
          itemStyle: { color },
        });
      });

      const sortMap = getGameDataIndex(gameData);
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));

      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            const sumKey = data[0].axisValue;

            let timeText = '';
            if (params?.date_type) {
              timeText = formatDateFunc(params?.date_type)(sumKey);
            }

            return formatChartParamsDate({
              params: data,
              title: timeText,
              rightTitle: formatMessage({ id: tooltip }),
              listTitleFunc: (item: { seriesName: string }) =>
                getGameData(item.seriesName),
              isSort: true,
              valueFormat,
            });
          },
        },
        legend: {
          data: legendData,
          formatter: (params: string) => getGameData(params),
        },
        axisPointer: getAxisPointer((data) =>
          formatTimeXAxisText(data.value, params?.date_type),
        ),
        xAxis: {
          type: 'category',
          boundaryGap: ['20%', 0],
          data: xAxisData,
          axisLabel: {
            interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 10,
            showMaxLabel: true,
            formatter: (value: any) =>
              formatTimeXAxisText(value, params?.date_type),
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) =>
              !isEngagementRate
                ? echartsContent(value)
                : `${(value * 100).toFixed(0)}%`,
            fontSize: 11,
          },
        },
        series: seriesData,
      };

      return option;
    }
    return {};
  }, [
    hasStatistics,
    yAxisData,
    xAxisData,
    chartType,
    params?.date_type,
    formatMessage,
    tooltip,
    formatData,
  ]);

  return (
    <div ref={chartRef} className="dbo-mention-sentiment-chart">
      <CardCommon
        contentCard
        loading={loading}
        empty={!hasStatistics}
        emptyHeight={TableHeight.CHART_LOADING}
        loadingHeight={TableHeight.CHART_LOADING}
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            optionsProps={{ type: chartType }}
            height={TableHeight.CHART}
            leftTitle={formatMessage({ id: chartMap?.text || NO_DATE.EMPTY })}
          />
        ) : (
          <TableCommonHook
            recordKey={'comment_uin'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={columns}
            sortColumns={['time', 'value']}
            hasPageable={false}
            defaultSort={sortBy}
            changeSort={setSortBy}
            sortType={'local'}
            bordered="all"
            minWidth={'auto'}
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
